import React from 'react';
import { Container, Paper, Typography, Box, Alert, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageBackButton from '../../components/PageBackButton';

interface Section {
  heading: string;
  paragraphs: string[];
}

type LangKey = 'bg' | 'en' | 'nl' | 'tr' | 'pl';

const LAST_UPDATED = '18.08.2026';

const content: Record<LangKey, Section[]> = {
  nl: [
    {
      heading: '1. Verwerkingsverantwoordelijke',
      paragraphs: [
        'Verwerkingsverantwoordelijke voor persoonsgegevens is [Naam eigenaar], handelend onder de naam "MasterBook" (eenmanszaak, KVK-nummer: [in te vullen na registratie]), gevestigd te [adres], Nederland.',
        'Voor vragen over de verwerking van persoonsgegevens kunt u contact met ons opnemen via: privacy@masterbook.nl',
      ],
    },
    {
      heading: '2. Welke gegevens wij verzamelen',
      paragraphs: [
        'Bij registratie: e-mailadres, naam, gebruikerstype (klant/vakman), wachtwoord (versleuteld opgeslagen).',
        'Bij gebruik van het Platform: telefoonnummer, locatie, profielfoto, dienstenbeschrijving, berichten tussen gebruikers, gepubliceerde opdrachten en offertes, beoordelingen.',
        'Technische gegevens: IP-adres, apparaat- en browsertype, voor beveiligingsdoeleinden en fraudepreventie.',
      ],
    },
    {
      heading: '3. Rechtsgrondslag voor verwerking',
      paragraphs: [
        'Wij verwerken persoonsgegevens op basis van: uitvoering van een overeenkomst (levering van de Platformdiensten), toestemming van de gebruiker (bij registratie), gerechtvaardigd belang (beveiliging, fraudepreventie) en wettelijke verplichting (indien van toepassing).',
      ],
    },
    {
      heading: '4. Doeleinden van de verwerking',
      paragraphs: [
        'Aanmaken en beheren van een gebruikersaccount, het verbinden van Klanten met Vakmensen, communicatie tussen gebruikers, het versturen van accountgerelateerde meldingen en e-mails (bijv. wachtwoordherstel), en het verbeteren van de diensten en beveiliging van het Platform.',
      ],
    },
    {
      heading: '5. Bewaartermijn',
      paragraphs: [
        'Persoonsgegevens worden bewaard zolang het gebruikersaccount actief is. Bij een verzoek om verwijdering van het account worden de gegevens binnen een redelijke termijn gewist of geanonimiseerd, tenzij een wettelijke verplichting tot langere bewaring bestaat.',
      ],
    },
    {
      heading: '6. Delen van gegevens met derden',
      paragraphs: [
        'Wij maken gebruik van dienstverleners voor het versturen van transactionele e-mails (bijv. Brevo) en hosting van het Platform. Deze dienstverleners verwerken gegevens uitsluitend volgens onze instructies en in overeenstemming met de toepasselijke gegevensbeschermingswetgeving.',
        'Wij verkopen of delen geen persoonsgegevens met derden voor marketingdoeleinden zonder de expliciete toestemming van de gebruiker.',
      ],
    },
    {
      heading: '7. Rechten van de gebruiker',
      paragraphs: [
        'Op grond van de Algemene Verordening Gegevensbescherming (AVG/GDPR) heeft u recht op: inzage in uw persoonsgegevens, correctie van onjuiste gegevens, verwijdering van gegevens ("recht op vergetelheid"), beperking van de verwerking, gegevensoverdraagbaarheid en bezwaar tegen de verwerking.',
        'U kunt deze rechten uitoefenen door contact met ons op te nemen via privacy@masterbook.nl. U heeft het recht een klacht in te dienen bij de bevoegde toezichthoudende autoriteit, de Autoriteit Persoonsgegevens (AP) in Nederland.',
      ],
    },
    {
      heading: '8. Cookies',
      paragraphs: [
        'Het Platform kan cookies gebruiken die noodzakelijk zijn voor de werking ervan (bijv. het onderhouden van de sessie bij inloggen). Voor anonieme bezoekersstatistieken gebruiken wij een zelf-gehoste, cookieloze tool (Umami); deze plaatst geen trackingcookies. Voor analytische of marketingcookies van derden zullen wij uw expliciete toestemming vragen.',
      ],
    },
    {
      heading: '9. Beveiliging van gegevens',
      paragraphs: [
        'Wij passen passende technische en organisatorische maatregelen toe om persoonsgegevens te beschermen, waaronder versleuteling van wachtwoorden, toegangscontrole en beperking van onbevoegde toegangspogingen.',
      ],
    },
    {
      heading: '10. Kinderen',
      paragraphs: [
        'Het Platform is niet bedoeld voor personen jonger dan 18 jaar. Wij verzamelen niet bewust persoonsgegevens van minderjarigen.',
      ],
    },
    {
      heading: '11. Wijzigingen in dit beleid',
      paragraphs: [
        'Dit Privacybeleid kan periodiek worden bijgewerkt. Bij wezenlijke wijzigingen zullen wij gebruikers informeren via het Platform of per e-mail.',
      ],
    },
  ],
  en: [
    {
      heading: '1. Data controller',
      paragraphs: [
        'The data controller for personal data is [Owner name], trading as "MasterBook" (sole proprietorship / eenmanszaak, KVK number: [to be completed upon registration]), based in [address], the Netherlands.',
        'For questions about the processing of personal data, please contact us at: privacy@masterbook.nl',
      ],
    },
    {
      heading: '2. What data we collect',
      paragraphs: [
        'Upon registration: email address, name, user type (client/professional), password (stored encrypted).',
        'When using the Platform: phone number, location, profile picture, service description, messages between users, published tasks and bids, reviews and ratings.',
        'Technical data: IP address, device and browser type, for security purposes and fraud prevention.',
      ],
    },
    {
      heading: '3. Legal basis for processing',
      paragraphs: [
        'We process personal data on the following grounds: performance of a contract (provision of the Platform services), user consent (upon registration), legitimate interest (security, fraud prevention), and legal obligation (where applicable).',
      ],
    },
    {
      heading: '4. Purposes of processing',
      paragraphs: [
        'Creating and managing a user account, connecting Clients with Professionals, communication between users, sending account-related notifications and emails (e.g. password recovery), and improving the services and security of the Platform.',
      ],
    },
    {
      heading: '5. Retention period',
      paragraphs: [
        'Personal data is stored for as long as the user account is active. Upon request for account deletion, data is deleted or anonymized within a reasonable timeframe, unless a legal obligation requires longer retention.',
      ],
    },
    {
      heading: '6. Sharing data with third parties',
      paragraphs: [
        'We use service providers for sending transactional emails (e.g. Brevo) and hosting the Platform. These providers process data only according to our instructions and in compliance with applicable data protection legislation.',
        'We do not sell or share personal data with third parties for marketing purposes without the user\'s explicit consent.',
      ],
    },
    {
      heading: '7. User rights',
      paragraphs: [
        'Under the General Data Protection Regulation (GDPR), you have the right to: access your personal data, correct inaccurate data, request deletion ("right to be forgotten"), restrict processing, data portability, and object to processing.',
        'You may exercise these rights by contacting us at privacy@masterbook.nl. You have the right to lodge a complaint with the competent supervisory authority, the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).',
      ],
    },
    {
      heading: '8. Cookies',
      paragraphs: [
        'The Platform may use cookies necessary for its operation (e.g. maintaining the session upon login). For anonymous visit statistics we use a self-hosted, cookie-less tool (Umami); it does not set tracking cookies. For third-party analytics or marketing cookies, we will request your explicit consent.',
      ],
    },
    {
      heading: '9. Data security',
      paragraphs: [
        'We apply appropriate technical and organizational measures to protect personal data, including password encryption, access control, and limiting unauthorized access attempts.',
      ],
    },
    {
      heading: '10. Children',
      paragraphs: [
        'The Platform is not intended for persons under 18 years of age. We do not knowingly collect personal data from minors.',
      ],
    },
    {
      heading: '11. Changes to this policy',
      paragraphs: [
        'This Privacy Policy may be updated periodically. In the event of material changes, we will notify users via the Platform or by email.',
      ],
    },
  ],
  bg: [
    {
      heading: '1. Администратор на лични данни',
      paragraphs: [
        'Администратор на лични данни е [Име на собственика], търгуващ под името „MasterBook" (едноличен търговец / eenmanszaak, KVK номер: [ще се попълни при регистрация]), със седалище в [адрес], Нидерландия.',
        'За въпроси относно обработката на лични данни можете да се свържете с нас на: privacy@masterbook.nl',
      ],
    },
    {
      heading: '2. Какви данни събираме',
      paragraphs: [
        'При регистрация: имейл адрес, име, тип потребител (клиент/професионалист), парола (в криптиран вид).',
        'При използване на Платформата: телефон, местоположение, снимка на профил, описание на услуги, съобщения между потребители, публикувани задачи и оферти, отзиви и оценки.',
        'Технически данни: IP адрес, тип устройство и браузър, за целите на сигурността и предотвратяване на злоупотреби.',
      ],
    },
    {
      heading: '3. Правно основание за обработка',
      paragraphs: [
        'Обработваме личните данни на следните основания: изпълнение на договор (предоставяне на услугите на Платформата), съгласие на потребителя (при регистрация), легитимен интерес (сигурност, предотвратяване на измами) и законово задължение (когато е приложимо).',
      ],
    },
    {
      heading: '4. Цели на обработката',
      paragraphs: [
        'Създаване и управление на потребителски профил, свързване на Клиенти с Професионалисти, комуникация между потребители, изпращане на известия и имейли, свързани с профила (напр. възстановяване на парола), подобряване на услугите и сигурността на Платформата.',
      ],
    },
    {
      heading: '5. Срок на съхранение',
      paragraphs: [
        'Личните данни се съхраняват докато потребителският профил е активен. При заявка за изтриване на профила, данните се изтриват или анонимизират в разумен срок, освен ако не съществува законово задължение за по-дълго съхранение.',
      ],
    },
    {
      heading: '6. Споделяне на данни с трети страни',
      paragraphs: [
        'Използваме доставчици на услуги за изпращане на транзакционни имейли (напр. Brevo) и хостинг на Платформата. Тези доставчици обработват данни само по нашите инструкции и в съответствие с приложимото законодателство за защита на данните.',
        'Не продаваме и не споделяме лични данни с трети страни за маркетингови цели без изричното съгласие на потребителя.',
      ],
    },
    {
      heading: '7. Права на потребителя',
      paragraphs: [
        'Съгласно Общия регламент за защита на данните (GDPR), имате право на: достъп до личните си данни, коригиране на неточни данни, изтриване на данни („право да бъдеш забравен"), ограничаване на обработката, преносимост на данните и възражение срещу обработката.',
        'Можете да упражните тези права, като се свържете с нас на privacy@masterbook.nl. Имате право да подадете жалба до компетентния надзорен орган — Autoriteit Persoonsgegevens в Нидерландия.',
      ],
    },
    {
      heading: '8. Бисквитки (Cookies)',
      paragraphs: [
        'Платформата може да използва бисквитки, необходими за нейното функциониране (напр. поддържане на сесията при вход). За анонимна статистика на посещенията използваме собствено хостван инструмент без бисквитки за следене (Umami). При въвеждане на анализни или маркетингови бисквитки от трети страни ще поискаме изричното ви съгласие.',
      ],
    },
    {
      heading: '9. Сигурност на данните',
      paragraphs: [
        'Прилагаме подходящи технически и организационни мерки за защита на личните данни, включително криптиране на пароли, контрол на достъпа и ограничаване на опитите за неоторизиран достъп.',
      ],
    },
    {
      heading: '10. Деца',
      paragraphs: [
        'Платформата не е предназначена за лица под 18 години. Не събираме съзнателно лични данни на непълнолетни лица.',
      ],
    },
    {
      heading: '11. Промени в политиката',
      paragraphs: [
        'Тази Политика за поверителност може да бъде актуализирана периодично. При съществени промени ще уведомим потребителите чрез Платформата или по имейл.',
      ],
    },
  ],
  tr: [
    {
      heading: '1. Veri sorumlusu',
      paragraphs: [
        'Kişisel verilerin veri sorumlusu, "MasterBook" ticari unvanıyla faaliyet gösteren [Sahip adı]\'dır (eenmanszaak, KVK numarası: [kayıt sonrası doldurulacak]), [adres], Hollanda.',
        'Kişisel verilerin işlenmesiyle ilgili sorular için: privacy@masterbook.nl',
      ],
    },
    {
      heading: '2. Hangi verileri topluyoruz',
      paragraphs: [
        'Kayıtta: e-posta adresi, ad, kullanıcı türü (müşteri/profesyonel), şifre (şifrelenmiş olarak saklanır).',
        'Platformu kullanırken: telefon, konum, profil fotoğrafı, hizmet açıklaması, kullanıcılar arası mesajlar, yayımlanan görevler ve teklifler, yorumlar ve puanlar.',
        'Teknik veriler: güvenlik ve dolandırıcılığın önlenmesi için IP adresi, cihaz ve tarayıcı türü.',
      ],
    },
    {
      heading: '3. İşlemenin hukuki dayanağı',
      paragraphs: [
        'Kişisel verileri şu dayanaklarla işleriz: sözleşmenin ifası (Platform hizmetleri), kullanıcı rızası (kayıt), meşru menfaat (güvenlik, dolandırıcılığın önlenmesi) ve yasal yükümlülük (uygulanabildiği yerde).',
      ],
    },
    {
      heading: '4. İşleme amaçları',
      paragraphs: [
        'Hesap oluşturma ve yönetimi, Müşterileri Profesyonellerle eşleştirme, kullanıcılar arası iletişim, hesapla ilgili bildirim ve e-postalar (ör. şifre sıfırlama) ile Platform hizmetlerinin ve güvenliğinin iyileştirilmesi.',
      ],
    },
    {
      heading: '5. Saklama süresi',
      paragraphs: [
        'Kişisel veriler, kullanıcı hesabı aktif olduğu sürece saklanır. Hesap silme talebinde veriler makul süre içinde silinir veya anonimleştirilir; yasal yükümlülük daha uzun saklamayı gerektiriyorsa bu kural uygulanmaz.',
      ],
    },
    {
      heading: '6. Üçüncü taraflarla paylaşım',
      paragraphs: [
        'İşlemsel e-postalar (ör. Brevo) ve Platform barındırma için hizmet sağlayıcıları kullanırız. Bu sağlayıcılar verileri yalnızca talimatlarımız doğrultusunda ve yürürlükteki veri koruma mevzuatına uygun işler.',
        'Kişisel verileri, kullanıcının açık rızası olmadan pazarlama amacıyla üçüncü taraflara satmaz veya paylaşmayız.',
      ],
    },
    {
      heading: '7. Kullanıcı hakları',
      paragraphs: [
        'Genel Veri Koruma Tüzüğü (GDPR) kapsamında şu haklara sahipsiniz: kişisel verilere erişim, yanlış verilerin düzeltilmesi, silme talebi ("unutulma hakkı"), işlemeyi kısıtlama, veri taşınabilirliği ve işlemeye itiraz.',
        'Bu hakları privacy@masterbook.nl adresinden kullanabilirsiniz. Yetkili denetim otoritesi olan Hollanda Autoriteit Persoonsgegevens\'e şikayette bulunma hakkınız vardır.',
      ],
    },
    {
      heading: '8. Çerezler',
      paragraphs: [
        'Platform, çalışması için gerekli çerezleri kullanabilir (ör. oturumun sürdürülmesi). Anonim ziyaret istatistikleri için kendi barındırılan, çerez kullanmayan bir araç (Umami) kullanırız; izleme çerezi koymaz. Üçüncü taraf analitik veya pazarlama çerezleri için açık rızanızı isteriz.',
      ],
    },
    {
      heading: '9. Veri güvenliği',
      paragraphs: [
        'Kişisel verileri korumak için uygun teknik ve idari önlemler uygularız: şifre şifreleme, erişim denetimi ve yetkisiz erişim girişimlerinin sınırlanması.',
      ],
    },
    {
      heading: '10. Çocuklar',
      paragraphs: [
        'Platform 18 yaşından küçük kişilere yönelik değildir. Bilerek reşit olmayanlardan kişisel veri toplamayız.',
      ],
    },
    {
      heading: '11. Bu politikadaki değişiklikler',
      paragraphs: [
        'Bu Gizlilik Politikası dönemsel olarak güncellenebilir. Önemli değişikliklerde kullanıcıları Platform veya e-posta yoluyla bilgilendiririz.',
      ],
    },
  ],
  pl: [
    {
      heading: '1. Administrator danych',
      paragraphs: [
        'Administratorem danych osobowych jest [Imię właściciela], działający jako „MasterBook" (eenmanszaak, numer KVK: [do uzupełnienia po rejestracji]), z siedzibą pod adresem [adres], Holandia.',
        'W sprawach przetwarzania danych osobowych skontaktuj się z nami: privacy@masterbook.nl',
      ],
    },
    {
      heading: '2. Jakie dane zbieramy',
      paragraphs: [
        'Przy rejestracji: adres e-mail, imię, typ użytkownika (klient/profesjonalista), hasło (przechowywane w postaci zaszyfrowanej).',
        'Podczas korzystania z Platformy: telefon, lokalizacja, zdjęcie profilowe, opis usług, wiadomości między użytkownikami, opublikowane zadania i oferty, recenzje i oceny.',
        'Dane techniczne: adres IP, typ urządzenia i przeglądarki, w celach bezpieczeństwa i zapobiegania nadużyciom.',
      ],
    },
    {
      heading: '3. Podstawa prawna przetwarzania',
      paragraphs: [
        'Dane osobowe przetwarzamy na podstawie: wykonania umowy (świadczenie usług Platformy), zgody użytkownika (przy rejestracji), prawnie uzasadnionego interesu (bezpieczeństwo, zapobieganie oszustwom) oraz obowiązku prawnego (gdy ma zastosowanie).',
      ],
    },
    {
      heading: '4. Cele przetwarzania',
      paragraphs: [
        'Tworzenie i zarządzanie kontem, łączenie Klientów z Profesjonalistami, komunikacja między użytkownikami, wysyłka powiadomień i e-maili związanych z kontem (np. odzyskiwanie hasła) oraz poprawa usług i bezpieczeństwa Platformy.',
      ],
    },
    {
      heading: '5. Okres przechowywania',
      paragraphs: [
        'Dane osobowe są przechowywane tak długo, jak konto użytkownika jest aktywne. Po wniosku o usunięcie konta dane są usuwane lub anonimizowane w rozsądnym terminie, chyba że obowiązek prawny wymaga dłuższego przechowywania.',
      ],
    },
    {
      heading: '6. Udostępnianie danych stronom trzecim',
      paragraphs: [
        'Korzystamy z dostawców do wysyłki e-maili transakcyjnych (np. Brevo) i hostingu Platformy. Przetwarzają oni dane wyłącznie według naszych instrukcji i zgodnie z przepisami o ochronie danych.',
        'Nie sprzedajemy ani nie udostępniamy danych osobowych stronom trzecim do celów marketingowych bez wyraźnej zgody użytkownika.',
      ],
    },
    {
      heading: '7. Prawa użytkownika',
      paragraphs: [
        'Zgodnie z RODO masz prawo do: dostępu do danych, sprostowania nieprawidłowych danych, żądania usunięcia („prawo do bycia zapomnianym"), ograniczenia przetwarzania, przenoszenia danych oraz sprzeciwu wobec przetwarzania.',
        'Prawa te możesz wykonać, pisząc na privacy@masterbook.nl. Masz też prawo złożyć skargę do właściwego organu nadzorczego, holenderskiego Autoriteit Persoonsgegevens.',
      ],
    },
    {
      heading: '8. Pliki cookie',
      paragraphs: [
        'Platforma może używać plików cookie niezbędnych do działania (np. utrzymanie sesji po zalogowaniu). Do anonimowych statystyk odwiedzin używamy samodzielnie hostowanego narzędzia bez plików cookie śledzących (Umami). W przypadku analitycznych lub marketingowych cookie stron trzecich poprosimy o wyraźną zgodę.',
      ],
    },
    {
      heading: '9. Bezpieczeństwo danych',
      paragraphs: [
        'Stosujemy odpowiednie środki techniczne i organizacyjne ochrony danych, w tym szyfrowanie haseł, kontrolę dostępu i ograniczanie nieautoryzowanych prób logowania.',
      ],
    },
    {
      heading: '10. Dzieci',
      paragraphs: [
        'Platforma nie jest przeznaczona dla osób poniżej 18. roku życia. Świadomie nie zbieramy danych osobowych małoletnich.',
      ],
    },
    {
      heading: '11. Zmiany polityki',
      paragraphs: [
        'Niniejsza Polityka prywatności może być okresowo aktualizowana. Przy istotnych zmianach poinformujemy użytkowników przez Platformę lub e-mailem.',
      ],
    },
  ],
};

const SUPPORTED_LANGS: LangKey[] = ['bg', 'en', 'nl', 'tr', 'pl'];

const PrivacyPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] as LangKey;
  const lang: LangKey = SUPPORTED_LANGS.includes(currentLang) ? currentLang : 'nl';
  const sections = content[lang];

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 3, sm: 6 }, mb: 6, px: { xs: 2, sm: 3 } }}>
      <PageBackButton />
      <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('legal.privacy_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('legal.last_updated')}: {LAST_UPDATED}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          {t('legal.language_notice')}
        </Alert>

        {sections.map((section, idx) => (
          <Box key={section.heading} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {section.heading}
            </Typography>
            {section.paragraphs.map((p, pIdx) => (
              <Typography key={pIdx} variant="body1" sx={{ mb: 1, color: 'text.primary' }}>
                {p}
              </Typography>
            ))}
            {idx < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
